import {
  ListTotal,
  TableToolbar,
  TableWrapper,
  Table,
  TruncatedText,
  ReactionsPreview,
  Typography,
  Link,
  LinkAsButton,
  ActionsMenu,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  DateRangeFilter,
  EmployeesFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { DownloadXlsAction } from 'components/actions';
import {
  COLOR_BY_STATUS,
  getMissingSenderLabelKey,
  recognitionTypes,
  recognitionsFiltersSchema,
  useDownloadTemplate,
  useExportToXls,
  usePointsLabel,
  useRecognitions,
} from 'store/recognitions';
import { APP, ROUTES } from 'config';
import { useModalContext } from 'store/modal';
import { useSearchParams } from 'utils/navigation';
import { useRecognitionAchievements } from 'store/recognition-achievements';
import { ReactionsModal } from 'containers/reactions';
import { RecognitionThreadModal } from 'containers/recognitions';
import { useMe } from 'state/Administrators';
import { EmployeeCell } from 'components/table';
import { RecognitionActions, RecognitionsImportModal } from './atoms';

export const RecognitionsList = () => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const { hasEditPermission, hasViewPermission } = useMe();

  const { searchParams: filters, navigate } = useSearchParams(
    recognitionsFiltersSchema,
  );

  const { data: recognitions, isLoading } = useRecognitions(filters);

  const { data: achievements } = useRecognitionAchievements();
  const pointsLabel = usePointsLabel();

  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();
  const { mutate: downloadTemplate, isLoading: isDownloadingTemplate } =
    useDownloadTemplate();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap="15px"
          >
            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('actions.import'),
                  color: 'primary',
                  disabled:
                    !hasEditPermission(['employeeRecognition']) ||
                    !hasViewPermission(['employees']),
                  onClick: () => {
                    openModal({
                      component: RecognitionsImportModal,
                    });
                  },
                },
                {
                  label: t('global.actions.export_template'),
                  disabled:
                    isDownloadingTemplate ||
                    !hasEditPermission(['employeeRecognition']) ||
                    !hasViewPermission(['employees']),
                  onClick: () => {
                    downloadTemplate();
                  },
                },
              ]}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
          >
            <ListTotal total={recognitions?.total} />

            <Stack direction="row" alignItems="center" gap="10px">
              <Filters
                value={filters}
                initialValues={recognitionsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    {hasViewPermission(['employees']) ? (
                      <FilterButton
                        label={t('recognition.feedSender')}
                        value={value.sender}
                        initialValue={initialValues.sender}
                        onChange={(v) => setFilter('sender', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <EmployeesFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>
                    ) : null}

                    {hasViewPermission(['employees']) ? (
                      <FilterButton
                        label={t('recognition.feedRecipient')}
                        value={value.recipient}
                        initialValue={initialValues.recipient}
                        onChange={(v) => setFilter('recipient', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <EmployeesFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>
                    ) : null}

                    <FilterButton
                      label={t('integrations.type')}
                      value={value.type}
                      initialValue={initialValues.type}
                      onChange={(v) => setFilter('type', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          options={recognitionTypes.map((type) => ({
                            key: type,
                            label: t(`recognitions.type.${type}`),
                          }))}
                          searchable={false}
                          multiple={false}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('recognition.feedFiltersRecognitionDate')}
                      value={value.date}
                      initialValue={initialValues.date}
                      onChange={(v) => setFilter('date', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <DateRangeFilter
                          value={value}
                          onApply={applyFilter}
                          onClear={clearFilter}
                          onCancel={close}
                          withTimezone
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('recognition.feedFiltersType')}
                      value={value.achievements}
                      initialValue={initialValues.achievements}
                      onChange={(v) => setFilter('achievements', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          multiple
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          options={
                            achievements?.map((achievement) => ({
                              key: achievement.id,
                              label: achievement.name,
                            })) ?? []
                          }
                        />
                      )}
                    </FilterButton>

                    {hasViewPermission(['employees']) ? (
                      <OpenSearchFilter
                        value={value.employee}
                        onApplyFilter={(v) => setFilter('employee', v)}
                        hint={t('recognition.recipientsListFiltersSearchHint')}
                      />
                    ) : null}
                  </>
                )}
              </Filters>

              <Divider orientation="vertical" variant="middle" flexItem />

              <DownloadXlsAction
                listLength={recognitions?.total ?? 0}
                exportToExcel={() => exportToXls({ filters })}
                loading={isExportingToXls}
              />
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={recognitions?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'date',
            header: t('recognition.feedDate'),
            cell: ({ row: { original: recognition } }) =>
              moment(recognition.createdAt).format('l'),
            meta: { sort: true },
          },
          {
            id: 'sender',
            header: t('recognition.feedSender'),
            cell: ({ row: { original: recognition } }) => (
              <EmployeeCell
                employee={recognition.sender}
                fallback={t(getMissingSenderLabelKey(recognition.type))}
              />
            ),
          },
          {
            id: 'recipient',
            header: t('recognition.feedRecipient'),
            cell: ({ row: { original: recognition } }) => (
              <EmployeeCell employee={recognition.employee} />
            ),
          },
          {
            id: 'pointsReceived',
            header: pointsLabel,
            cell: ({ row: { original: recognition } }) =>
              recognition.pointsReceived,
            meta: { align: 'right' },
          },
          {
            id: 'achievement',
            header: t('recognition.feedAchievement'),
            cell: ({ row: { original: recognition } }) => {
              if (recognition.achievement?.id) {
                return (
                  <Link
                    label={recognition.achievement.name}
                    to={ROUTES.recognitions.achievements.details.replace(
                      ':id',
                      recognition.achievement.id,
                    )}
                  />
                );
              }

              if (recognition.achievement?.name) {
                return recognition.achievement.name;
              }

              if (!recognition.survey) {
                return null;
              }

              const canViewSurveyOrTraining =
                recognition.survey.type === 'survey'
                  ? hasViewPermission(['surveys'])
                  : recognition.survey.type === 'training'
                  ? hasViewPermission(['trainings'])
                  : true;

              return canViewSurveyOrTraining ? (
                <Link
                  label={recognition.survey.name}
                  to={ROUTES.surveys[recognition.survey.type].results.replace(
                    ':id',
                    recognition.survey.id,
                  )}
                />
              ) : (
                recognition.survey.name
              );
            },
          },
          {
            id: 'comment',
            header: t('recognition.message.label'),
            cell: ({ row: { original: recognition } }) => (
              <Stack gap={0.25} alignItems="flex-start">
                <TruncatedText
                  text={recognition.comment}
                  maxLength={300}
                  textProps={{ color: 'inherit' }}
                />
                <ReactionsPreview
                  reactions={recognition.reactions.summary}
                  onClick={() => {
                    openModal({
                      component: ReactionsModal,
                      props: { hostId: recognition.id },
                    });
                  }}
                />
              </Stack>
            ),
          },
          {
            id: 'comments',
            header: '💬',
            cell: ({ row: { original: recognition } }) => {
              return (
                <LinkAsButton
                  label={recognition.commentsCount}
                  onClick={() => {
                    openModal({
                      component: RecognitionThreadModal,
                      props: {
                        recognitionId: recognition.id,
                      },
                      size: 'xl',
                    });
                  }}
                />
              );
            },
            meta: { align: 'right', sort: true },
          },
          {
            id: 'reactions',
            header: '👍',
            cell: ({ row: { original: recognition } }) => {
              return (
                <LinkAsButton
                  label={recognition.reactions.total}
                  onClick={() => {
                    openModal({
                      component: ReactionsModal,
                      props: { hostId: recognition.id },
                    });
                  }}
                />
              );
            },
            meta: { align: 'right', sort: true },
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: recognition } }) =>
              recognition.status === 'not_required' ? null : (
                <Typography color={COLOR_BY_STATUS[recognition.status]}>
                  {t(
                    `recognition.recognitionApprovalStatus-${recognition.status}`,
                  )}
                </Typography>
              ),
          },
          {
            id: 'actions',
            cell: ({ row: { original: recognition } }) => {
              return <RecognitionActions recognition={recognition} />;
            },
          },
        ]}
        pagination={
          recognitions && {
            count: recognitions.total,
            page: recognitions.page,
            pageSize: recognitions.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
      />
    </TableWrapper>
  );
};
